import { useEffect, useState } from 'react';
import { usersApi } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { Users, UserPlus, Trash2, Eye, EyeOff } from 'lucide-react';

interface PortalUser {
  id: string;
  username: string;
  email: string;
  first_name?: string;
  last_name?: string;
  role: string;
  is_active: boolean;
  created_at: string;
}

const ROLES = [
  { value: 'viewer', label: 'Viewer', description: 'Read-only access' },
  { value: 'editor', label: 'Editor', description: 'Can update credential values' },
  { value: 'admin', label: 'Admin', description: 'Full access incl. user management' },
];

const emptyForm = {
  username: '',
  email: '',
  password: '',
  role: 'viewer',
  first_name: '',
  last_name: '',
};

export default function UsersPage() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<PortalUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ ...emptyForm });
  const [showPassword, setShowPassword] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const loadUsers = () => {
    usersApi
      .getAll()
      .then((res) => setUsers(res.data.data.users || []))
      .catch(() => setError('Failed to load users'))
      .finally(() => setLoading(false));
  };

  useEffect(loadUsers, []);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);
    setCreateError(null);
    try {
      await usersApi.create({
        username: form.username,
        email: form.email,
        password: form.password,
        role: form.role,
        first_name: form.first_name || undefined,
        last_name: form.last_name || undefined,
      });
      setForm({ ...emptyForm });
      setShowCreate(false);
      loadUsers();
    } catch (err: any) {
      setCreateError(err.response?.data?.error?.message || 'Failed to create user');
    } finally {
      setCreating(false);
    }
  };

  const handleRoleChange = async (id: string, role: string) => {
    try {
      await usersApi.update(id, { role });
      loadUsers();
    } catch {
      setError('Failed to update role');
    }
  };

  const handleToggleActive = async (u: PortalUser) => {
    try {
      await usersApi.update(u.id, { is_active: !u.is_active });
      loadUsers();
    } catch {
      setError('Failed to update user');
    }
  };

  const handleDelete = async (u: PortalUser) => {
    if (!window.confirm(`Delete user "${u.username}"? This cannot be undone.`)) return;
    try {
      await usersApi.remove(u.id);
      loadUsers();
    } catch {
      setError('Failed to delete user');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-10 w-10 border-4 border-brand-orange border-t-transparent" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-white mb-1">User Management</h2>
          <p className="text-gray-400">
            Create client accounts and control what each user can do.
          </p>
        </div>
        <button className="btn btn-primary flex items-center" onClick={() => setShowCreate((s) => !s)}>
          <UserPlus size={18} className="mr-2" /> New User
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-500/10 border border-red-500/50 rounded-lg text-red-400 text-sm">
          {error}
        </div>
      )}

      {showCreate && (
        <form onSubmit={handleCreate} className="card mb-6">
          <h3 className="text-lg font-semibold text-white mb-4">Create User</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-gray-300 text-sm font-medium mb-1">Username</label>
              <input
                className="input"
                required
                value={form.username}
                onChange={(e) => setForm({ ...form, username: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-gray-300 text-sm font-medium mb-1">Email</label>
              <input
                type="email"
                className="input"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-gray-300 text-sm font-medium mb-1">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  className="input pr-10"
                  required
                  minLength={8}
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <div>
              <label className="block text-gray-300 text-sm font-medium mb-1">Role</label>
              <select
                className="input"
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })}
              >
                {ROLES.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label} — {r.description}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-gray-300 text-sm font-medium mb-1">First name</label>
              <input
                className="input"
                value={form.first_name}
                onChange={(e) => setForm({ ...form, first_name: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-gray-300 text-sm font-medium mb-1">Last name</label>
              <input
                className="input"
                value={form.last_name}
                onChange={(e) => setForm({ ...form, last_name: e.target.value })}
              />
            </div>
          </div>

          {createError && (
            <div className="mt-4 p-3 bg-red-500/10 border border-red-500/50 rounded-lg text-red-400 text-sm">
              {createError}
            </div>
          )}

          <div className="mt-4 flex gap-3">
            <button type="submit" disabled={creating} className="btn btn-primary disabled:opacity-50">
              {creating ? 'Creating…' : 'Create User'}
            </button>
            <button type="button" className="btn btn-secondary" onClick={() => setShowCreate(false)}>
              Cancel
            </button>
          </div>
        </form>
      )}

      {users.length === 0 ? (
        <div className="card text-center py-12">
          <Users className="mx-auto text-gray-600 mb-4" size={40} />
          <p className="text-gray-400">No users found.</p>
        </div>
      ) : (
        <div className="card overflow-x-auto p-0">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-gray-700 text-gray-400 text-sm">
                <th className="px-6 py-3 font-medium">User</th>
                <th className="px-6 py-3 font-medium">Email</th>
                <th className="px-6 py-3 font-medium">Role</th>
                <th className="px-6 py-3 font-medium">Status</th>
                <th className="px-6 py-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const isSelf = u.id === currentUser?.id;
                return (
                  <tr key={u.id} className="border-b border-gray-700/50 text-sm">
                    <td className="px-6 py-3">
                      <p className="text-white font-medium">{u.username}</p>
                      {(u.first_name || u.last_name) && (
                        <p className="text-gray-500 text-xs">
                          {[u.first_name, u.last_name].filter(Boolean).join(' ')}
                        </p>
                      )}
                    </td>
                    <td className="px-6 py-3 text-gray-300">{u.email}</td>
                    <td className="px-6 py-3">
                      <select
                        className="input py-1 text-sm w-auto"
                        value={u.role}
                        disabled={isSelf}
                        onChange={(e) => handleRoleChange(u.id, e.target.value)}
                        title={isSelf ? 'You cannot change your own role' : 'Change role'}
                      >
                        {ROLES.map((r) => (
                          <option key={r.value} value={r.value}>
                            {r.label}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-6 py-3">
                      <button
                        onClick={() => !isSelf && handleToggleActive(u)}
                        disabled={isSelf}
                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                          u.is_active
                            ? 'bg-green-500/20 text-green-400'
                            : 'bg-gray-500/20 text-gray-400'
                        } ${isSelf ? 'cursor-default' : 'hover:opacity-80'}`}
                        title={isSelf ? '' : u.is_active ? 'Click to deactivate' : 'Click to activate'}
                      >
                        {u.is_active ? 'Active' : 'Disabled'}
                      </button>
                    </td>
                    <td className="px-6 py-3 text-right">
                      {!isSelf && (
                        <button
                          onClick={() => handleDelete(u)}
                          className="p-2 text-gray-400 hover:text-red-400 transition-colors"
                          title="Delete user"
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
